/**
 * LVS 77-3:2016 ceļa zīmju krāsu palete
 *
 * Krāsas atbilst LVS EN 12899-1 un Latvijas ceļa zīmju tradicionālajam
 * tonējumam. RAL kodi ir standarta Eiropas ekvivalenti, HEX vērtības —
 * ekrāna attēlojumam (sRGB).
 */

export type LvsColorKey =
    | 'white'
    | 'black'
    | 'red'
    | 'blue'
    | 'green'
    | 'yellow'
    | 'brown'
    | 'grey'
    | 'orange'

export interface LvsColor {
    readonly key: LvsColorKey
    readonly label: string
    readonly description: string
    readonly ral: string
    readonly hex: string
    /** CMYK percentages, each 0..100. Source of truth for press output. */
    readonly cmyk100: readonly [number, number, number, number]
}

export const LVS_COLORS: Readonly<Record<LvsColorKey, LvsColor>> = {
    white: {
        key: 'white',
        label: 'Balts',
        description:
            'Pamatlaukums brīdinājuma, aizlieguma un papildzīmēm; teksts uz zila vai zaļa fona',
        ral: 'RAL 9016',
        hex: '#F7F9F9',
        cmyk100: [1, 0, 0, 2]
    },
    black: {
        key: 'black',
        label: 'Melns',
        description: 'Simboli, teksts, apmales un bultas',
        ral: 'RAL 9017',
        hex: '#1E1E1E',
        cmyk100: [0, 0, 0, 100]
    },
    red: {
        key: 'red',
        label: 'Sarkans',
        description: 'Apmales brīdinājuma un aizlieguma zīmēm, STOP zīmes fons, diagonālās svītras',
        ral: 'RAL 3020',
        hex: '#CC0605',
        cmyk100: [0, 100, 100, 0]
    },
    blue: {
        key: 'blue',
        label: 'Zils',
        description: 'Pamatlaukums rīkojuma, norādījuma, servisa un virziena zīmēm',
        ral: 'RAL 5017',
        hex: '#005387',
        cmyk100: [100, 40, 0, 0]
    },
    green: {
        key: 'green',
        label: 'Zaļš',
        description: 'Pamatlaukums automaģistrāļu virziena zīmēm',
        ral: 'RAL 6024',
        hex: '#008754',
        cmyk100: [90, 0, 100, 5]
    },
    yellow: {
        key: 'yellow',
        label: 'Dzeltens',
        description: 'Priekšrocības zīmes 201 centrs, pagaidu un būvdarbu zīmes',
        ral: 'RAL 1023',
        hex: '#F7B500',
        cmyk100: [0, 5, 100, 0]
    },
    brown: {
        key: 'brown',
        label: 'Brūns',
        description: 'Tūrisma un apskates objektu virziena zīmes',
        ral: 'RAL 8002',
        hex: '#7B5544',
        cmyk100: [50, 100, 100, 0]
    },
    grey: {
        key: 'grey',
        label: 'Pelēks',
        description: 'Zīmes aizmugure (izņemot alumīnija pamatni) — LVS 77-3:2016, 10.1.',
        ral: 'RAL 7042',
        hex: '#8F9695',
        cmyk100: [5, 0, 1, 41]
    },
    orange: {
        key: 'orange',
        label: 'Oranžs',
        description: 'Pagaidu ceļa darbu zīmes',
        ral: 'RAL 2009',
        hex: '#E15501',
        cmyk100: [0, 45, 100, 0]
    }
} as const

export const LVS_COLOR_KEYS: readonly LvsColorKey[] = [
    'white',
    'black',
    'red',
    'blue',
    'green',
    'yellow',
    'brown',
    'grey',
    'orange'
] as const

/** Relatīvais spilgtums pēc ITU-R BT.709 — true, ja krāsa ir tumša (teksts uz tās būtu balts). */
export function isDarkColor(hex: string): boolean {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5
}

/** Render a CMYK tuple as the human-readable "C / M / Y / K" string used on screen. */
export function formatCmykDisplay(cmyk: LvsColor['cmyk100']): string {
    return cmyk.join(' / ')
}
